import axios, { AxiosResponse } from 'axios';
import {
  Proprietaire,
  Animal,
  Veterinaire,
  Clinique,
  Consultation,
  Prescription,
  Medicament,
  Traitement,
  Rappel,
  SuiviQuotidien,
  PaginatedResponse,
  ProprietaireFormData,
  AnimalFormData,
  ConsultationFormData,
  VeterinaireAvecDistance
} from '../types';
import { API_BASE_URL } from '../config';

// Configuration de base d'Axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification si disponible
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur pour gérer les erreurs globalement
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Services pour les propriétaires
export const proprietaireService = {
  getAll: (): Promise<AxiosResponse<PaginatedResponse<Proprietaire>>> =>
    api.get('proprietaires/'),
  
  getById: (id: number): Promise<AxiosResponse<Proprietaire>> =>
    api.get(`proprietaires/${id}/`),
  
  create: (data: ProprietaireFormData): Promise<AxiosResponse<Proprietaire>> =>
    api.post('proprietaires/', data),
  
  update: (id: number, data: Partial<ProprietaireFormData>): Promise<AxiosResponse<Proprietaire>> =>
    api.patch(`proprietaires/${id}/`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`proprietaires/${id}/`),
};

// Services pour les animaux
export const animalService = {
  getAll: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<Animal>>> =>
    api.get('animaux/', { params }),
  
  getById: (id: number): Promise<AxiosResponse<Animal>> =>
    api.get(`animaux/${id}/`),
  
  create: (data: AnimalFormData): Promise<AxiosResponse<Animal>> =>
    api.post('animaux/', data),
  
  update: (id: number, data: Partial<AnimalFormData>): Promise<AxiosResponse<Animal>> =>
    api.patch(`animaux/${id}/`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`animaux/${id}/`),
  
  getByProprietaire: (proprietaireId: number): Promise<AxiosResponse<PaginatedResponse<Animal>>> =>
    api.get('animaux/', { params: { proprietaire: proprietaireId } }),
};

// Services pour les vétérinaires
export const veterinaireService = {
  getAll: (): Promise<AxiosResponse<PaginatedResponse<Veterinaire>>> =>
    api.get('veterinaires/'),
  
  getById: (id: number): Promise<AxiosResponse<Veterinaire>> =>
    api.get(`veterinaires/${id}/`),
  
  create: (data: Partial<Veterinaire>): Promise<AxiosResponse<Veterinaire>> =>
    api.post('veterinaires/', data),
  
  update: (id: number, data: Partial<Veterinaire>): Promise<AxiosResponse<Veterinaire>> =>
    api.patch(`veterinaires/${id}/`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`veterinaires/${id}/`),

  // Rechercher les vétérinaires proches d'une position
  getVeterinairesProches: (
    latitude: number, 
    longitude: number, 
    rayon: number = 10
  ): Promise<AxiosResponse<PaginatedResponse<VeterinaireAvecDistance>>> =>
    api.get('veterinaires/proches/', {
      params: { latitude, longitude, rayon }
    }),
};

// Services pour les cliniques
export const cliniqueService = {
  getAll: (): Promise<AxiosResponse<PaginatedResponse<Clinique>>> =>
    api.get('cliniques/'),
  
  getById: (id: number): Promise<AxiosResponse<Clinique>> =>
    api.get(`cliniques/${id}/`),
  
  create: (data: Partial<Clinique>): Promise<AxiosResponse<Clinique>> =>
    api.post('cliniques/', data),
  
  update: (id: number, data: Partial<Clinique>): Promise<AxiosResponse<Clinique>> =>
    api.patch(`cliniques/${id}/`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`cliniques/${id}/`),
};

// Services pour les consultations
export const consultationService = {
  getAll: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<Consultation>>> =>
    api.get('consultations/', { params }),
  
  getById: (id: number): Promise<AxiosResponse<Consultation>> =>
    api.get(`consultations/${id}/`),
  
  create: (data: ConsultationFormData): Promise<AxiosResponse<Consultation>> =>
    api.post('consultations/', data),
  
  update: (id: number, data: Partial<ConsultationFormData>): Promise<AxiosResponse<Consultation>> =>
    api.patch(`consultations/${id}/`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`consultations/${id}/`),
  
  getByAnimal: (animalId: number): Promise<AxiosResponse<PaginatedResponse<Consultation>>> =>
    api.get('consultations/', { params: { animal: animalId } }),
};

// Services pour les prescriptions
export const prescriptionService = {
  getAll: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<Prescription>>> =>
    api.get('prescriptions/', { params }),
  
  getById: (id: number): Promise<AxiosResponse<Prescription>> =>
    api.get(`prescriptions/${id}/`),
  
  create: (data: Partial<Prescription>): Promise<AxiosResponse<Prescription>> =>
    api.post('prescriptions/', data),
  
  update: (id: number, data: Partial<Prescription>): Promise<AxiosResponse<Prescription>> =>
    api.patch(`prescriptions/${id}/`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`prescriptions/${id}/`),
};

// Services pour les médicaments
export const medicamentService = {
  getAll: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<Medicament>>> =>
    api.get('medicaments/', { params }),
  
  getById: (id: number): Promise<AxiosResponse<Medicament>> =>
    api.get(`medicaments/${id}/`),
  
  create: (data: Partial<Medicament>): Promise<AxiosResponse<Medicament>> =>
    api.post('medicaments/', data),
  
  update: (id: number, data: Partial<Medicament>): Promise<AxiosResponse<Medicament>> =>
    api.patch(`medicaments/${id}/`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`medicaments/${id}/`),
  
  search: (query: string): Promise<AxiosResponse<PaginatedResponse<Medicament>>> =>
    api.get('medicaments/', { params: { search: query } }),
};

// Services pour les traitements
export const traitementService = {
  getAll: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<Traitement>>> =>
    api.get('traitements/', { params }),
  
  getById: (id: number): Promise<AxiosResponse<Traitement>> =>
    api.get(`traitements/${id}/`),
  
  create: (data: Partial<Traitement>): Promise<AxiosResponse<Traitement>> =>
    api.post('traitements/', data),
  
  update: (id: number, data: Partial<Traitement>): Promise<AxiosResponse<Traitement>> =>
    api.patch(`traitements/${id}/`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`traitements/${id}/`),
  
  getByAnimal: (animalId: number): Promise<AxiosResponse<PaginatedResponse<Traitement>>> =>
    api.get('traitements/', { params: { animal: animalId } }),
  
  getActifs: (): Promise<AxiosResponse<PaginatedResponse<Traitement>>> =>
    api.get('traitements/', { params: { actif: true } }),
};

// Services pour les rappels
export const rappelService = {
  getAll: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<Rappel>>> =>
    api.get('rappels/', { params }),
  
  getById: (id: number): Promise<AxiosResponse<Rappel>> =>
    api.get(`rappels/${id}/`),
  
  create: (data: Partial<Rappel>): Promise<AxiosResponse<Rappel>> =>
    api.post('rappels/', data),
  
  update: (id: number, data: Partial<Rappel>): Promise<AxiosResponse<Rappel>> =>
    api.patch(`rappels/${id}/`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`rappels/${id}/`),
  
  markAsCompleted: (id: number): Promise<AxiosResponse<Rappel>> =>
    api.patch(`rappels/${id}/`, { effectue: true, date_execution: new Date().toISOString() }),
  
  getPending: (): Promise<AxiosResponse<PaginatedResponse<Rappel>>> =>
    api.get('rappels/', { params: { effectue: false } }),
  
  getToday: (): Promise<AxiosResponse<PaginatedResponse<Rappel>>> => {
    const today = new Date().toISOString().split('T')[0];
    return api.get('rappels/', { 
      params: { 
        date_heure__date: today,
        effectue: false 
      } 
    });
  },
};

// Services pour le suivi quotidien
export const suiviQuotidienService = {
  getAll: (params?: Record<string, any>): Promise<AxiosResponse<PaginatedResponse<SuiviQuotidien>>> =>
    api.get('suivis/', { params }),
  
  getById: (id: number): Promise<AxiosResponse<SuiviQuotidien>> =>
    api.get(`suivis/${id}/`),
  
  create: (data: Partial<SuiviQuotidien>): Promise<AxiosResponse<SuiviQuotidien>> =>
    api.post('suivis/', data),
  
  update: (id: number, data: Partial<SuiviQuotidien>): Promise<AxiosResponse<SuiviQuotidien>> =>
    api.patch(`suivis/${id}/`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`suivis/${id}/`),
  
  getByAnimal: (animalId: number): Promise<AxiosResponse<PaginatedResponse<SuiviQuotidien>>> =>
    api.get('suivis/', { params: { animal: animalId } }),
  
  getByDate: (date: string): Promise<AxiosResponse<PaginatedResponse<SuiviQuotidien>>> =>
    api.get('suivis/', { params: { date_suivi: date } }),
};

// Export de l'instance axios configurée pour des cas d'usage spéciaux
export default api; 